import { Inject, Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import * as updateActions from '../actions/update-consent.actions';
import * as enableActions from '../actions/enable-consent.actions';
import * as consentActions from '../actions/consent.actions';
import * as message from '../../../core/actions/message.actions';
import { ConsentRestClient } from '../services/consent-rest-client.service';
import { Consent } from '../model/consent.model';
import { EnableConsent } from '../model/enable-consent.model';
import { StatusRestClient } from '../../../core/services/status-rest-client.service';
import { AppMessage } from '../../../core/model/app-message.model';
import { AuthorizationStatus } from '../../../core/model/authorization/authorization-status.model';
import { AppState } from '../../../app.state';
import { selectEnableConsentAuthAuthorization } from '../consent.selector';
import { pollChecker, PollCheckerResult, PollCheckerStatus } from '../../../core/helpers/poll-checker.helper';
import { HttpErrorResponse } from '@angular/common/http';
import { SignInfo } from '../../../core/model/authorization/sign-info.model';
import { getAppMessageType } from '../../../core/helpers/status-to-type.helper';
import { AuthorizationStatusEnum } from '../../../core/model/authorization/authorization-status.enum';

import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/withLatestFrom';
import 'rxjs/add/observable/of';

/**
 * Side effects related to Enable Consent Authorization Process.
 */
@Injectable()
export class EnableConsentEffects {

    /**
     * Side effect to trigger enable consent.
     */
    @Effect() triggerEnableConsent$ = this.actions$
        .ofType(updateActions.ENABLE_CONSENT)
        .map(() => new enableActions.EnableConsentRequestAction());

    /**
     * Side effect to fire request to enable consent.
     */
    @Effect() addRequest$ = this.actions$
        .ofType(enableActions.ENABLE_CONSENT_REQUEST)
        .switchMap(() => this.enable());

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Side effect to finish the authorization - trigger get status from API.
     */
    @Effect() triggerGetStatus$ = this.actions$
        .ofType(enableActions.ENABLE_CONSENT_AUTH_FINISHED)
        .withLatestFrom(this.store.select(selectEnableConsentAuthAuthorization))
        .map(([, auth]: ([enableActions.EnableConsentAuthFinishedAction, EnableConsent])) =>
            new enableActions.EnableConsentStatusRequestAction(auth.signInfo));

    /**
     * Side effect to get authorization status from API.
     */
    @Effect() getStatus$ = this.actions$
        .ofType(enableActions.ENABLE_CONSENT_STATUS_REQUEST)
        .switchMap((action: enableActions.EnableConsentStatusRequestAction) => pollChecker<AuthorizationStatus | HttpErrorResponse>(
            this.retrieveStatus.bind(this, action.payload),
            this.repeatStatus, this.initDelay, this.repeatDelay, this.maxRepeatCount))
        .mergeMap((result: PollCheckerResult<AuthorizationStatus | HttpErrorResponse>) => this.mapPollResult(result));

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Side effect to refresh consent as a result of authorization status success.
     */
    @Effect() statusSuccess$ = this.actions$
        .ofType(enableActions.ENABLE_CONSENT_STATUS_SUCCESS)
        .mergeMap((action: enableActions.EnableConsentStatusSuccessAction) =>
            [
                new message.MessageShowAction(AppMessage.newMessage(
                    getAppMessageType(action.payload.status),
                    'CONSENT.ENABLE-CONSENT-AUTH.' + action.payload.status)),
                new consentActions.LoadRequestAction(),
                new enableActions.EnableConsentCleanAction()
            ]
        );

    /**
     * Side effect to refresh consent as a result of authorization status error.
     */
    @Effect() statusError$ = this.actions$
        .ofType(enableActions.ENABLE_CONSENT_STATUS_ERROR)
        .mergeMap(() =>
            [
                new message.MessageShowAction(AppMessage.newWarningMessage('SHARED.AUTH.STATUS-NOT-VERIFIED')),
                new consentActions.LoadRequestAction(),
                new enableActions.EnableConsentCleanAction()
            ]
        );

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Authorization error.
     */
    @Effect() authError$ = this.actions$
        .ofType(enableActions.ENABLE_CONSENT_AUTH_ERROR)
        .map((action: enableActions.EnableConsentAuthErrorAction) =>
            new message.MessageShowAction(AppMessage.newErrorMessage('SHARED.AUTH.' + action.payload)));

    // -----------------------------------------------------------------------------------------------------------------

    private enable() {
        return this.client.updateConsent(<Consent>{consent: true})
            .map((res: EnableConsent) => (new enableActions.EnableConsentSuccessAction(res)))
            // If request fails, dispatch failed action
            .catch(error => Observable.of(new enableActions.EnableConsentErrorAction(error)));
    }

    // -----------------------------------------------------------------------------------------------------------------

    private retrieveStatus(signInfo: SignInfo): Observable<AuthorizationStatus | HttpErrorResponse> {
        return this.status
            .getStatus(signInfo)
            .catch(err => Observable.of(err));
    }

    private repeatStatus(status: AuthorizationStatus) {
        return !status.finished  &&  status.status === AuthorizationStatusEnum.AUTHORIZED;
    }

    private mapPollResult(result): Observable<Action> {
        if (PollCheckerStatus.FINISHED === result.status) {
            return Observable.of(new enableActions.EnableConsentStatusSuccessAction(result.result));
        } else if (PollCheckerStatus.REACHED_MAX_ATTEMPTS === result.status) {
            return Observable.of(new enableActions.EnableConsentStatusErrorAction(result));
        }
    }

    constructor(private store: Store<AppState>,
                private client: ConsentRestClient,
                private status: StatusRestClient,
                private actions$: Actions,
                @Inject('initDelay') private initDelay: number,
                @Inject('repeatDelay') private repeatDelay: number,
                @Inject('maxRepeatCount') private maxRepeatCount: number) {}

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/modules/consent/effects/enable-consent.effects.ts